/**
 * VAMS-544 — SEO pagination UX/URL sync layer.
 *
 * Syncs `?page=N` with scroll position via replaceState (no history entry), without
 * touching Load More / infinite scroll (per Storeis request, ref. Peuterey.com).
 *
 * Scrolling updates `?page=N` to the page dominating the viewport, derived from the
 * `data-plp-index` stamp on cards (stable across scroll/reload). At the top, the param is
 * dropped. On deep-link `?page=N`, product loading is deterministic on the React side; here
 * we scroll to page N's first card (`data-plp-index === (N-1)*PAGE_SIZE`).
 *
 * NB: page size MUST stay aligned with `plp_seo_page_size` (collection-seo-pagination.liquid)
 * and `seo_pagination_page_size` (collection-seo-robots.liquid).
 */

import { isEmpty } from 'remeda'
import {
  getPageFromUrl,
  isDeepLinkSettled,
  PLP_PAGE_SIZE,
} from './plpPagination'

// offset from the top (sticky header) below which we consider a product "at the top"
const TOP_THRESHOLD_PX = 120

// true after the user's first real interaction (wheel/touch/keys). The URL is
// updated ONLY afterwards: programmatic scrolls and layout-shift drift (images loading)
// must not rewrite ?page on load.
let userInteracted = false

const markInteracted = () => {
  userInteracted = true
}

const getCollectionContainer = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('.apparel-macro-collection') ??
  document.querySelector<HTMLElement>('.apparel-collection') ??
  document.querySelector<HTMLElement>('#apparel-macro-category') ??
  document.querySelector<HTMLElement>('#apparel-category')

// total products from the Liquid mount (leaf = number, macro = JSON array of counts)
const getTotalProducts = (): number => {
  const leaf = document.querySelector<HTMLElement>('#apparel-category')
  const macro = document.querySelector<HTMLElement>('#apparel-macro-category')
  const raw =
    leaf?.getAttribute('data-products-count') ??
    macro?.getAttribute('data-products-count') ??
    ''

  try {
    const parsed = JSON.parse(raw)

    if (Array.isArray(parsed)) {
      return parsed.reduce((sum: number, n) => sum + (Number(n) || 0), 0)
    }

    return Number(parsed) || 0
  } catch {
    return Number(raw) || 0
  }
}

const getTotalPages = (): number => {
  const total = getTotalProducts()

  return total > 0 ? Math.ceil(total / PLP_PAGE_SIZE) : 1
}

const writePageToUrl = (page: number) => {
  const url = new URL(window.location.href)

  if (page <= 1) {
    url.searchParams.delete('page')
  } else {
    url.searchParams.set('page', String(page))
  }

  // avoid useless writes if the URL doesn't change
  if (url.href === window.location.href) {
    return
  }

  // VAMS-544: ALWAYS replaceState. The URL reflects the scroll position, it's not
  // a real navigation (there's no "go to page N" click) → it must not create a history
  // entry, otherwise scroll oscillation (2↔3) clogs up the Back button.
  window.history.replaceState(window.history.state, '', url.href)
}

// VAMS-544: product cards marked by React with `data-plp-card`, in DOM order.
const getStampedCards = (container: HTMLElement): HTMLElement[] => [
  ...container.querySelectorAll<HTMLElement>('[data-plp-card]'),
]

const pageFromIndex = (index: number): number =>
  Math.floor(index / PLP_PAGE_SIZE) + 1

const computeVisiblePage = (
  container: HTMLElement,
  totalPages: number,
): number => {
  const cards = getStampedCards(container)

  if (isEmpty(cards)) {
    return getPageFromUrl()
  }

  // "dominant page": the one with the most cards visible in the viewport. The page index is
  // the card's POSITION in DOM order (contiguous, no gaps), not an offset derived from the raw
  // count → no page jumps; and stable across scroll and reload because every section loads
  // all of its products, so a card's position never changes.
  const viewportHeight = window.innerHeight
  const counts = new Map<number, number>()

  cards.forEach((card, index) => {
    const rect = card.getBoundingClientRect()
    const isVisible =
      rect.bottom > TOP_THRESHOLD_PX && rect.top < viewportHeight

    if (isVisible) {
      const page = pageFromIndex(index)

      counts.set(page, (counts.get(page) ?? 0) + 1)
    }
  })

  if (counts.size === 0) {
    return getPageFromUrl()
  }

  // pages are inserted in increasing order: with a strict ">", ties are won by the lowest one
  let dominantPage = 1
  let max = -1

  counts.forEach((count, page) => {
    if (count > max) {
      max = count
      dominantPage = page
    }
  })

  return Math.min(Math.max(dominantPage, 1), totalPages)
}

// VAMS-544 deep-link: positions the viewport on page N's first card (DOM position
// `(N-1)*PAGE_SIZE` among `data-plp-card` ones). React loads sections deterministically on
// deep-link, so brief polling then a single instant scroll suffices. The programmatic scroll
// doesn't set userInteracted, so it won't trigger onScroll or fight a real user scroll.
const scrollToDeepLinkPage = (container: HTMLElement, page: number) => {
  const targetIndex = (page - 1) * PLP_PAGE_SIZE
  // ~8s: macro sections load async and OUT of order, so card[targetIndex] keeps changing
  // until loading is done (seen in preview: 66→106 cards, target shifting between products).
  const maxAttempts = 40
  // number of consecutive ticks with an UNCHANGED CARD COUNT to consider the async sections
  // "settled". VAMS-544 (iter1): counts cards (data-plp-card), NOT scrollHeight —
  // the latter was pinned by the anti-CLS height reservation and caused premature settles.
  const requiredStableTicks = 3
  let attempts = 0
  let lastCount = -1
  let stableTicks = 0

  // aligns the viewport to the target card (below the sticky header). Returns false if absent.
  const alignToTarget = (): boolean => {
    const cards = getStampedCards(container)
    const target = cards[targetIndex]

    if (!target) {
      return false
    }

    const top =
      target.getBoundingClientRect().top + window.scrollY - TOP_THRESHOLD_PX

    window.scrollTo({ top, behavior: 'auto' })

    return true
  }

  // after the initial alignment, images loading above shift the rows: re-align to the target
  // on every layout change, until the user takes control or the layout settles (max 3s). Safe:
  // the programmatic scroll doesn't set userInteracted → onScroll doesn't rewrite ?page during
  // the re-pin.
  const keepPinned = () => {
    const observer = new ResizeObserver(() => {
      if (userInteracted) {
        observer.disconnect()

        return
      }

      alignToTarget()
    })

    observer.observe(container)
    window.setTimeout(() => {
      observer.disconnect()
    }, 3000)
  }

  const findThenPin = () => {
    if (userInteracted) {
      return
    }

    const count = getStampedCards(container).length

    // VAMS-544 (iter1): "settled" = card count unchanged for N ticks. Counts cards, not
    // scrollHeight, which the anti-CLS height reservation pinned into premature settles.
    // Cards have a fixed aspect-ratio (ApparelProduct.styles), so loading images doesn't
    // shift rows — a stable card count is sufficient to align the target.
    if (count === lastCount && count > 0) {
      stableTicks = stableTicks + 1
    } else {
      stableTicks = 0
      lastCount = count
    }

    const settled = isDeepLinkSettled({
      cardCount: count,
      targetIndex,
      stableTicks,
      requiredStableTicks,
    })

    // if alignToTarget fails (target momentarily absent) the && short-circuits: we continue
    // with the retry/fallback below instead of stopping → no silent landing at the top.
    if (settled && alignToTarget()) {
      keepPinned()

      return
    }

    attempts = attempts + 1

    if (attempts >= maxAttempts) {
      // fallback: settle not detected in time → align to what's there, better than the top.
      if (count > targetIndex) {
        alignToTarget()
        keepPinned()
      }

      return
    }

    window.setTimeout(findThenPin, 200)
  }

  window.setTimeout(findThenPin, 200)
}

export const initSeoPaginationUrlSync = () => {
  const container = getCollectionContainer()

  if (!container) {
    return
  }

  // VAMS-544: MACROs are pure infinite scroll, the SEO ?page/deep-link layer stays only
  // on LEAVES.
  if (document.querySelector('#apparel-macro-category')) {
    return
  }

  // VAMS-544: idempotency guard. The bundle can be included/run twice by the theme
  // (helpers.js + app.js) → without this we'd get double listeners and two competing
  // deep-link loops. The flag on <html> is shared across the module's copies.
  if (document.documentElement.dataset.seoPaginationInit === '1') {
    return
  }
  document.documentElement.dataset.seoPaginationInit = '1'

  const totalPages = getTotalPages()

  // normalizes ?page=1 (or invalid values) to the canonical URL without the parameter
  const initialPage = getPageFromUrl()

  let currentPage = initialPage
  let scheduled = false

  // VAMS-544: product loading is deterministic on the React side. Here: page≤1 →
  // normalize to a clean URL; deep-link page>1 → position the viewport on page N.
  if (initialPage <= 1) {
    writePageToUrl(1)
  } else {
    scrollToDeepLinkPage(container, Math.min(initialPage, totalPages))
  }

  const onScroll = () => {
    // updates the URL ONLY after a real interaction: this way layout-shift drift
    // (images loading) on load doesn't strip ?page
    if (!userInteracted || scheduled) {
      return
    }
    scheduled = true
    window.requestAnimationFrame(() => {
      scheduled = false
      const page = computeVisiblePage(container, totalPages)

      if (page !== currentPage) {
        currentPage = page
        writePageToUrl(page)
      }
    })
  }

  window.addEventListener('wheel', markInteracted, { passive: true })
  window.addEventListener('touchmove', markInteracted, { passive: true })
  window.addEventListener('keydown', markInteracted, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })
}
